import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Send, 
  Twitter, 
  Music2, 
  Plus,
  Check,
  Trash2,
  ExternalLink,
  AlertCircle,
  Link2,
  Facebook,
  Instagram,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  Zap,
  MessageCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { 
    id: 'telegram', 
    name: 'Telegram', 
    icon: Send, 
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
    description: 'Connect your Telegram Bot to post to channels and groups.',
    fields: [
      { key: 'bot_token', label: 'Bot Token', placeholder: 'Enter your Telegram Bot Token', type: 'password' },
      { key: 'channel_id', label: 'Channel ID', placeholder: '@channelname or -100xxxxxxxxxx', type: 'text' }
    ],
    helpUrl: 'https://core.telegram.org/bots#how-do-i-create-a-bot'
  },
  { 
    id: 'twitter', 
    name: 'Twitter', 
    icon: Twitter, 
    color: 'text-sky-400',
    bgColor: 'bg-sky-500/10',
    borderColor: 'border-sky-500/20',
    description: 'Connect your Twitter Developer account to post tweets.',
    fields: [
      { key: 'api_key', label: 'API Key', placeholder: 'Your Twitter API Key', type: 'password' },
      { key: 'api_secret', label: 'API Secret', placeholder: 'Your Twitter API Secret', type: 'password' },
      { key: 'access_token', label: 'Access Token', placeholder: 'Your Access Token', type: 'password' },
      { key: 'access_secret', label: 'Access Token Secret', placeholder: 'Your Access Token Secret', type: 'password' }
    ],
    helpUrl: 'https://developer.twitter.com/en/docs/twitter-api'
  },
  { 
    id: 'facebook', 
    name: 'Facebook', 
    icon: Facebook, 
    color: 'text-blue-500',
    bgColor: 'bg-blue-600/10',
    borderColor: 'border-blue-600/20',
    description: 'Connect your Facebook Page to publish posts with images.',
    usePageSelector: true,  // Enable automatic page token extraction
    fields: [
      { key: 'user_token', label: 'User Access Token', placeholder: 'Paste your Facebook User Access Token here', type: 'password', isUserToken: true }
    ],
    helpUrl: 'https://developers.facebook.com/docs/pages/getting-started',
    note: 'Get your User Token from Graph API Explorer, then select a page below'
  },
  { 
    id: 'instagram', 
    name: 'Instagram', 
    icon: Instagram, 
    color: 'text-pink-500',
    bgColor: 'bg-gradient-to-br from-purple-500/10 to-pink-500/10',
    borderColor: 'border-pink-500/20',
    description: 'Connect your Instagram Business account (via Facebook Page) to publish posts.',
    fields: [
      { key: 'instagram_account_id', label: 'Instagram Business Account ID', placeholder: 'Your Instagram Business Account ID', type: 'text' },
      { key: 'page_token', label: 'Page Access Token', placeholder: 'Facebook Page Access Token with Instagram permissions', type: 'password' }
    ],
    helpUrl: 'https://developers.facebook.com/docs/instagram-api/getting-started',
    note: 'Requires: Facebook Page linked to Instagram Business account + instagram_basic & instagram_content_publish permissions'
  },
  { 
    id: 'tiktok', 
    name: 'TikTok', 
    icon: Music2, 
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/20',
    description: 'Connect your TikTok account to upload videos. Note: TikTok ONLY supports video posts.',
    fields: [],  // No manual fields - uses OAuth flow
    useOAuth: true,  // Flag to indicate OAuth flow
    helpUrl: 'https://developers.tiktok.com/',
    note: 'TikTok only supports VIDEO uploads. Images and text-only posts are not supported.'
  },
  { 
    id: 'discord_webhook', 
    name: 'Discord (Webhook)', 
    icon: MessageCircle, 
    color: 'text-indigo-400',
    bgColor: 'bg-indigo-500/10',
    borderColor: 'border-indigo-500/20',
    description: 'Easiest setup! Just paste a webhook URL to post to a Discord channel.',
    fields: [
      { key: 'webhook_url', label: 'Webhook URL', placeholder: 'https://discord.com/api/webhooks/...', type: 'password' },
      { key: 'username', label: 'Bot Username (Optional)', placeholder: 'Custom name for posts', type: 'text' }
    ],
    helpUrl: 'https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks',
    note: 'Simple setup - one webhook per channel',
    setupGuide: `**How to Create a Discord Webhook:**
1. Open Discord and go to your server
2. Click the channel name → Edit Channel (⚙️)
3. Go to "Integrations" → "Webhooks"
4. Click "New Webhook"
5. Give it a name and optionally upload an avatar
6. Click "Copy Webhook URL"
7. Paste the URL here!`
  },
  { 
    id: 'discord_bot', 
    name: 'Discord (Bot)', 
    icon: MessageCircle, 
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-600/10',
    borderColor: 'border-indigo-600/20',
    description: 'Advanced setup. Use a bot to post to any channel in your server.',
    fields: [
      { key: 'bot_token', label: 'Bot Token', placeholder: 'Your Discord Bot Token', type: 'password' },
      { key: 'channel_id', label: 'Channel ID', placeholder: 'e.g., 1234567890123456789', type: 'text' }
    ],
    helpUrl: 'https://discord.com/developers/applications',
    note: 'More powerful - bot can post to multiple channels',
    setupGuide: `**How to Create a Discord Bot:**
1. Go to Discord Developer Portal (discord.com/developers/applications)
2. Click "New Application" → Give it a name
3. Go to "Bot" section → Click "Add Bot"
4. Under "TOKEN", click "Copy" (keep this secret!)
5. Enable "MESSAGE CONTENT INTENT" under Privileged Intents
6. Go to "OAuth2" → "URL Generator"
7. Select scopes: "bot" and permissions: "Send Messages", "Embed Links", "Attach Files"
8. Copy the generated URL and open it to add bot to your server
9. Right-click the channel → "Copy Channel ID" (enable Developer Mode in Discord settings first)
10. Paste bot token and channel ID here!`
  },
];

const ConnectedAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [accountName, setAccountName] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [accountToDisconnect, setAccountToDisconnect] = useState(null);
  
  // Test connection state
  const [testingAccount, setTestingAccount] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  
  // Facebook page selector state
  const [fetchingPages, setFetchingPages] = useState(false);
  const [availablePages, setAvailablePages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  
  // Instagram linking state
  const [linkingInstagram, setLinkingInstagram] = useState(null);
  
  // Token health alerts state
  const [tokenAlerts, setTokenAlerts] = useState([]);
  const [loadingHealth, setLoadingHealth] = useState(false);

  // Link Instagram to Facebook account
  const linkInstagram = async (accountId) => {
    setLinkingInstagram(accountId);
    try {
      const response = await axios.post(`${API}/accounts/${accountId}/link-instagram`);
      toast.success(response.data.message || 'Instagram linked successfully!');
      fetchAccounts(); // Refresh accounts
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to link Instagram';
      toast.error(message);
    }
    setLinkingInstagram(null);
  };

  const fetchTokenHealth = async () => {
    try {
      const response = await axios.get(`${API}/accounts/health`);
      setTokenAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch token health:', error);
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      await axios.post(`${API}/accounts/health/dismiss/${alertId}`);
      setTokenAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success('Alert dismissed');
    } catch (error) {
      toast.error('Failed to dismiss alert');
    }
  };

  const getAlertStyles = (severity) => {
    switch (severity) {
      case 'expired':
        return 'bg-red-500/10 border-red-500/30 text-red-400';
      case 'critical':
        return 'bg-orange-500/10 border-orange-500/30 text-orange-400';
      case 'warning':
        return 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400';
      case 'error':
        return 'bg-red-500/10 border-red-500/30 text-red-400';
      default:
        return 'bg-muted/30 border-border/50 text-muted-foreground';
    }
  };

  const getAlertIcon = (severity) => {
    switch (severity) {
      case 'expired':
      case 'error':
        return <XCircle className="w-5 h-5" />;
      case 'critical':
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const fetchFacebookPages = async (userToken) => {
    if (!userToken) {
      toast.error('Please enter your User Access Token first');
      return;
    }
    
    setFetchingPages(true);
    setAvailablePages([]);
    setSelectedPage(null);
    
    try {
      const response = await axios.post(`${API}/accounts/facebook/get-pages`, {
        user_token: userToken
      });
      
      if (response.data.pages && response.data.pages.length > 0) {
        setAvailablePages(response.data.pages);
        toast.success(`Found ${response.data.pages.length} page(s)!`);
      } else {
        toast.error('No pages found. Make sure you granted page access when generating the token.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch pages');
      setAvailablePages([]);
    } finally {
      setFetchingPages(false);
    }
  };

  const selectFacebookPage = (page) => {
    setSelectedPage(page);
    // Auto-fill the credentials
    setCredentials({
      page_id: page.page_id,
      page_token: page.page_token
    });
    toast.success(`Selected: ${page.page_name}`);
  };

  const testConnection = async (accountId) => {
    setTestingAccount(accountId);
    setTestResult(null);
    setTestDialogOpen(true);
    
    try {
      const response = await axios.post(`${API}/accounts/${accountId}/test`);
      setTestResult(response.data);
    } catch (error) {
      setTestResult({
        success: false,
        checks: [{
          name: "Connection Test",
          status: "error",
          message: error.response?.data?.detail || "Failed to test connection"
        }]
      });
    } finally {
      setTestingAccount(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'info':
        return <Info className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchTokenHealth();  // Fetch token health alerts
    
    // Check for OAuth callback parameters
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const error = params.get('error');
    const platform = params.get('platform');
    const account = params.get('account');
    const message = params.get('message');
    
    if (success === 'true' && platform) {
      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} account "${account || 'Unknown'}" connected successfully!`);
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (error) {
      toast.error(`Connection failed: ${message || error}`);
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      toast.error('Failed to load connected accounts');
    } finally {
      setLoading(false);
    }
  };

  const getAccountsForPlatform = (platformId) => {
    return accounts.filter(acc => acc.platform === platformId);
  };

  const openConnectDialog = async (platform) => {
    // For OAuth platforms (like TikTok), redirect to OAuth flow instead of opening dialog
    if (platform.useOAuth) {
      setConnecting(true);
      try {
        const response = await axios.get(`${API}/${platform.id}/auth-url`);
        const authUrl = response.data.auth_url;
        if (authUrl) {
          toast.info(`Redirecting to ${platform.name} for authorization...`);
          window.location.href = authUrl;
        } else {
          toast.error('Failed to get authorization URL');
        }
      } catch (error) {
        const message = error.response?.data?.detail || `Failed to start ${platform.name} authorization`;
        toast.error(message);
      } finally {
        setConnecting(false);
      }
      return;
    }
    
    // For non-OAuth platforms, open the credential dialog
    setSelectedPlatform(platform);
    setCredentials({});
    setAccountName('');
    setAvailablePages([]);  // Reset page selector
    setSelectedPage(null);
    setConnectDialogOpen(true);
  };

  const handleConnect = async () => {
    if (!accountName.trim()) {
      toast.error('Please enter an account name');
      return;
    }

    // For platforms with page selector, check if page is selected
    if (selectedPlatform?.usePageSelector) {
      if (!selectedPage) {
        toast.error('Please fetch pages and select one to connect');
        return;
      }
      // Use the selected page credentials
    } else {
      // Validate all required fields for other platforms
      for (const field of selectedPlatform.fields) {
        if (!credentials[field.key]?.trim()) {
          toast.error(`Please enter ${field.label}`);
          return;
        }
      }
    }

    setConnecting(true);

    try {
      await axios.post(`${API}/accounts`, {
        platform: selectedPlatform.id,
        account_name: accountName,
        credentials: credentials
      });

      toast.success(`${selectedPlatform.name} account "${accountName}" connected!`);
      setConnectDialogOpen(false);
      fetchAccounts();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to connect account';
      toast.error(message);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!accountToDisconnect) return;

    try {
      await axios.delete(`${API}/accounts/${accountToDisconnect}`);
      toast.success('Account disconnected successfully');
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to disconnect account');
    } finally {
      setDisconnectDialogOpen(false);
      setAccountToDisconnect(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="connected-accounts-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Connected Accounts</h1>
        <p className="text-muted-foreground">Manage your social media platform connections. You can connect multiple accounts per platform.</p>
      </div>

      {/* Info Banner */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm">
              <strong>Tip:</strong> Connect multiple channels/accounts per platform. When creating a post, you can choose which accounts to publish to.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Token Health Alerts */}
      {tokenAlerts.length > 0 && (
        <div className="space-y-3" data-testid="token-health-alerts">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Token Health Alerts
          </h2>
          {tokenAlerts.map((alert) => (
            <Card 
              key={alert.id} 
              className={`border ${getAlertStyles(alert.severity)}`}
              data-testid={`alert-${alert.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    {getAlertIcon(alert.severity)}
                    <div>
                      <p className="font-medium">
                        {alert.platform === 'facebook' ? <Facebook className="w-4 h-4 inline mr-1" /> : <Instagram className="w-4 h-4 inline mr-1" />}
                        {alert.account_name}
                      </p>
                      <p className="text-sm mt-1">{alert.message}</p>
                      {alert.days_until_expiry !== null && alert.days_until_expiry > 0 && (
                        <p className="text-xs mt-1 opacity-75">
                          Expires: {new Date(alert.expires_at).toLocaleDateString()}
                        </p>
                      )}
                      {alert.severity === 'expired' && (
                        <p className="text-xs mt-2 font-medium">
                          Action needed: Reconnect this account using the "Add Account" button
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissAlert(alert.id)}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid={`dismiss-alert-${alert.id}`}
                  >
                    Dismiss
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Platform Sections */}
      <div className="space-y-6">
        {PLATFORMS.map((platform) => {
          const Icon = platform.icon;
          const platformAccounts = getAccountsForPlatform(platform.id);
          const hasAccounts = platformAccounts.length > 0;

          return (
            <Card 
              key={platform.id}
              className="bg-card border-border/50"
              data-testid={`platform-section-${platform.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-xl ${platform.bgColor}`}>
                      <Icon className={`w-6 h-6 ${platform.color}`} />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{platform.name}</CardTitle>
                      <CardDescription>{platform.description}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasAccounts && (
                      <Badge className="bg-accent/10 text-accent border-accent/20">
                        {platformAccounts.length} connected
                      </Badge>
                    )}
                    <Button 
                      size="sm"
                      onClick={() => openConnectDialog(platform)}
                      data-testid={`add-${platform.id}-btn`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Account
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {hasAccounts ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {platformAccounts.map((account) => (
                      <div 
                        key={account.id}
                        className="p-4 rounded-lg bg-muted/30 border border-border/50"
                        data-testid={`account-${account.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-accent" />
                            <p className="font-medium">{account.account_name}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                              onClick={() => testConnection(account.id)}
                              disabled={testingAccount === account.id}
                              data-testid={`test-${account.id}-btn`}
                            >
                              {testingAccount === account.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Zap className="w-4 h-4" />
                              )}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => {
                                setAccountToDisconnect(account.id);
                                setDisconnectDialogOpen(true);
                              }}
                              data-testid={`disconnect-${account.id}-btn`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Instagram Link Status for Facebook accounts */}
                        {platform.id === 'facebook' && (
                          <div className="mt-2 pt-2 border-t border-border/30">
                            {account.credentials?.instagram_account_id ? (
                              <div className="flex items-center gap-2 text-xs text-pink-400">
                                <Instagram className="w-3 h-3" />
                                <span>Instagram linked: @{account.credentials?.instagram_username || 'Connected'}</span>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full text-xs border-pink-500/50 text-pink-400 hover:bg-pink-500/10"
                                onClick={() => linkInstagram(account.id)}
                                disabled={linkingInstagram === account.id}
                              >
                                {linkingInstagram === account.id ? (
                                  <>
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Linking...
                                  </>
                                ) : (
                                  <>
                                    <Instagram className="w-3 h-3 mr-1" />
                                    Link Instagram Account
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                        
                        <p className="text-xs text-muted-foreground font-mono mt-2">
                          Connected {format(new Date(account.connected_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <p className="text-sm mb-2">No {platform.name} accounts connected yet</p>
                    <a 
                      href={platform.helpUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      How to get API credentials
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary */}
      <Card className="bg-card border-border/50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <Link2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="font-semibold">
                {accounts.length} total account{accounts.length !== 1 ? 's' : ''} connected
              </p>
              <p className="text-sm text-muted-foreground">
                {PLATFORMS.map(p => {
                  const count = getAccountsForPlatform(p.id).length;
                  return count > 0 ? `${p.name}: ${count}` : null;
                }).filter(Boolean).join(' • ') || 'Connect accounts to start posting'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connect Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedPlatform && (
                <>
                  <selectedPlatform.icon className={`w-5 h-5 ${selectedPlatform.color}`} />
                  Add {selectedPlatform?.name} Account
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Enter your API credentials to connect a {selectedPlatform?.name} account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Setup Guide for Discord */}
            {selectedPlatform?.setupGuide && (
              <div className="p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg max-h-48 overflow-y-auto">
                <p className="text-sm text-indigo-300 whitespace-pre-line">
                  {selectedPlatform.setupGuide}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="account_name">Account Name</Label>
              <Input
                id="account_name"
                placeholder="e.g., My Channel, Business Account"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                data-testid="account-name-input"
              />
              <p className="text-xs text-muted-foreground">Give this account a name to identify it</p>
            </div>

            {/* TikTok specific note */}
            {selectedPlatform?.id === 'tiktok' && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-400">
                  <strong>Important:</strong> TikTok only supports <strong>VIDEO</strong> uploads. 
                  Images and text-only posts will fail. Make sure to attach a video file (.mp4, .mov) when posting.
                </p>
              </div>
            )}

            {selectedPlatform?.fields.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key}>{field.label}</Label>
                <div className="flex gap-2">
                  <Input
                    id={field.key}
                    type={field.type}
                    placeholder={field.placeholder}
                    value={credentials[field.key] || ''}
                    onChange={(e) => setCredentials(prev => ({ ...prev, [field.key]: e.target.value }))}
                    data-testid={`credential-${field.key}-input`}
                    className="flex-1"
                  />
                  {/* Show "Fetch Pages" button for Facebook user_token field */}
                  {field.isUserToken && selectedPlatform?.usePageSelector && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => fetchFacebookPages(credentials[field.key])}
                      disabled={fetchingPages || !credentials[field.key]}
                    >
                      {fetchingPages ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Fetch Pages'
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {/* Facebook Page Selector */}
            {selectedPlatform?.usePageSelector && availablePages.length > 0 && (
              <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border/50">
                <Label>Select a Page to Connect</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {availablePages.map((page) => (
                    <div
                      key={page.page_id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedPage?.page_id === page.page_id
                          ? 'border-primary bg-primary/10'
                          : 'border-border/50 hover:border-primary/50 hover:bg-muted/50'
                      }`}
                      onClick={() => selectFacebookPage(page)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{page.page_name}</p>
                          <p className="text-xs text-muted-foreground">ID: {page.page_id}</p>
                        </div>
                        {selectedPage?.page_id === page.page_id && (
                          <CheckCircle className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      {page.has_instagram && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-pink-400">
                          <Instagram className="w-3 h-3" />
                          <span>Instagram: @{page.instagram_username}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {selectedPage && (
                  <div className="text-xs text-green-400 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Page Token auto-filled! Ready to connect.
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConnect} disabled={connecting} data-testid="submit-connect-btn">
              {connecting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Connecting...
                </span>
              ) : (
                'Connect Account'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disconnect this account? You won't be able to publish to it until you reconnect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDisconnect}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-disconnect-btn"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Test Connection Results Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Connection Test Results
            </DialogTitle>
            <DialogDescription>
              {testResult?.account_name} ({testResult?.platform})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {testingAccount && !testResult && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <span className="ml-3">Testing connection...</span>
              </div>
            )}
            
            {testResult && (
              <>
                {/* Overall Status */}
                <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="font-medium text-green-400">Connection is working!</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-5 h-5 text-red-500" />
                        <span className="font-medium text-red-400">Connection has issues</span>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Individual Checks */}
                <div className="space-y-2">
                  {testResult.checks?.map((check, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-3 p-3 rounded-lg bg-muted/30"
                    >
                      {getStatusIcon(check.status)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{check.name}</p>
                        <p className="text-xs text-muted-foreground break-words">{check.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setTestDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConnectedAccounts;
